import { motion } from 'framer-motion'
import { Calendar, MapPin, Building, Award } from 'lucide-react'

const Experience = () => {
  const experiences = [
    {
      title: "Founder & CEO",
      company: "NovoUp Technologies",
      period: "August 2022 - Present",
      location: "Dallas, Texas",
      description: "Spearheaded the development of AI-enabled applications for healthcare and insurance, driving digital transformation and delivering client value through innovation in LLMs, knowledge retrieval, and automation.",
      achievements: [
        "Spearheaded the development of AI-enabled applications for healthcare and insurance, driving digital transformation and delivering client value through innovation in LLMs, knowledge retrieval, and automation",
        "Defined the company's technology vision and strategic roadmap, ensuring solutions were designed with scalability, resilience, and governance at the core",
        "Directed cross-functional agile teams through the full product lifecycle, from ideation and architecture to deployment and continuous improvement, with a focus on secure and compliant delivery",
        "Championed the use of advanced analytics and intelligent automation to streamline operations, improve decision-making, and enhance business resilience",
        "Drove client engagement and business growth, translating complex requirements into practical technology strategies while embedding security and compliance awareness in every engagement",
        "Designed and rolled out enterprise data governance programs for clients, establishing frameworks, organizational roles, and clear accountability models to strengthen data integrity and stewardship",
        "Built dashboards and KPI-driven reporting to measure adoption, monitor quality, and provide executive visibility into governance program health",
        "Implemented metadata, classification, and data quality standards, ensuring data was properly cataloged, secured, and governed across its lifecycle",
        "Developed and deployed a structured intake and governance workflow, enabling smoother adoption, stakeholder training, and consistent application of governance principles",
        "Led structured risk assessments and business impact analyses, identifying vulnerabilities, prioritizing threats, and guiding remediation strategies to improve resilience",
        "Authored and implemented data and security policies for clients, addressing privacy, continuity, and compliance expectations in regulated environments"
      ],
      technologies: ["AI/ML", "LLMs", "Healthcare Technology", "Insurance Technology", "Data Governance", "Risk Management", "Compliance", "Digital Transformation"]
    },
    {
      title: "VP - Engineering",
      company: "Medecision",
      period: "March 2021 - August 2022",
      location: "Dallas, Texas",
      description: "Owned and directed the comprehensive Care and Case Management software portfolio, providing strategic leadership for 7 engineering teams responsible for the architecture, development, and maintenance of critical healthcare applications.",
      achievements: [
        "Owned and directed the comprehensive Care and Case Management software portfolio, providing strategic leadership for 7 engineering teams responsible for the architecture, development, and maintenance of critical healthcare applications with a strong focus on reliability, security, and compliance",
        "Managed the full lifecycle of diverse applications within the portfolio, including solutions for Utilization Management (UM), Disease Management (DM), Appeals & Grievances, and Patient Engagement platforms, ensuring alignment with clinical best practices and regulatory expectations while maintaining robust governance processes",
        "Drove modernization and enhancement of the portfolio by integrating AI/ML capabilities for predictive analytics in patient risk stratification and optimizing workflows for care coordinators and case managers, while embedding data protection and risk considerations into design",
        "Served as a key change agent in the organization's transformation to Enterprise Agile practices (SAFe), fostering customer-centricity, transparency, and improved governance across product development and delivery",
        "Led and mentored high-performing engineering teams to build and maintain secure, scalable, and high-performance applications and services, directly improving client and patient experiences",
        "Managed departmental reporting, portfolio-specific metrics, and financial analysis, articulating risks, trends, and strategic recommendations to senior leadership while ensuring operational and compliance oversight",
        "Partnered extensively with product management, UI/UX, client success, and sales teams on customer-driven enhancements, resolution of complex issues, and new product innovations, balancing innovation with security and governance considerations",
        "Oversaw project execution across the engineering organization, ensuring alignment with organizational goals, structured communication with stakeholders, and consistent accountability across initiatives",
        "Fostered an innovative engineering culture focused on collaboration, execution excellence, continuous improvement, and disciplined risk management"
      ],
      technologies: ["Healthcare Software", "Care Management", "Case Management", "AI/ML", "SAFe", "Agile", "Patient Engagement", "Utilization Management", "Disease Management", "Compliance"]
    },
    {
      title: "VP - Software Engineering",
      company: "JP Morgan Chase",
      period: "January 2017 - March 2021",
      location: "Dallas, Texas",
      description: "Provided technology leadership for multiple cloud-enabled applications, serving as application owner for high-impact systems with cross-line-of-business influence.",
      achievements: [
        "Architected and delivered a private enterprise search engine using event-driven architecture and microservices",
        "Pioneered the firm's adoption of accelerated development and deployment practices with cloud deployment strategies",
        "Established DevOps foundations by automating CI/CD pipelines, reducing build/deployment times from three days to three hours",
        "Led the transition from monolithic ETL solutions to event-based microservices, improving scalability and responsiveness",
        "Implemented proactive monitoring and alerting using Dynatrace APM, improving visibility into performance and operational risks",
        "Enhanced development productivity by embedding secure engineering practices into the SDLC, championing TDD and BDD"
      ],
      technologies: ["Cloud Architecture", "Microservices", "Kafka", "Cassandra", "DevOps", "CI/CD", "Dynatrace", "TDD/BDD"]
    },
    {
      title: "Director of Engineering",
      company: "Tillster",
      period: "February 2016 - January 2017",
      location: "San Diego, CA",
      description: "Directed the testing Center of Excellence (COE), owning tools and processes across the organization and ensuring they supported secure, scalable, and high-quality delivery.",
      achievements: [
        "Directed the testing Center of Excellence (COE), owning tools and processes across the organization",
        "Established and maintained manual, automated, and performance testing standards that strengthened confidence in applications",
        "Implemented automation frameworks (Selenium, Cucumber) that significantly increased development efficiency",
        "Led the performance engineering function, defining continuous improvement practices and incorporating structured security testing",
        "Partnered with DevOps teams to design and implement application monitoring and APM strategies across production environments",
        "Streamlined mobile testing in cloud environments, ensuring applications were validated for reliability and performance"
      ],
      technologies: ["Test Automation", "Selenium", "Cucumber", "Performance Testing", "APM", "Mobile Testing", "DevOps"]
    },
    {
      title: "Director Engineering",
      company: "Digital River",
      period: "August 2008 - February 2016",
      location: "Minnetonka, MN",
      description: "Built and led the performance engineering and test automation team, delivering a framework (EARTH) that standardized automated regression testing and improved coverage across platforms.",
      achievements: [
        "Built and led the performance engineering and test automation team, delivering the EARTH framework",
        "Served as technical owner for enterprise automation solutions using HP ALM, QTP/UFT, and Selenium Webdriver",
        "Introduced innovative approaches for using functional scripts in performance trend analysis",
        "Managed regression coverage for 20+ global platforms/applications, ensuring continuity of service",
        "Oversaw the adoption of proactive security testing tools, identifying vulnerabilities earlier in the development cycle",
        "Created KPI-based dashboards and release scorecards to monitor testing quality and provide executive updates"
      ],
      technologies: ["Performance Engineering", "Test Automation", "HP ALM", "QTP/UFT", "Selenium", "Security Testing", "KPI Dashboards"]
    },
    {
      title: "Software Engineer",
      company: "Real Vision Solutions",
      period: "March 2003 - August 2008",
      location: "Chicago, IL",
      description: "Delivered consulting and implementation services for enterprise clients, establishing foundations for test management suites that improved governance of development and release cycles.",
      achievements: [
        "Delivered consulting and implementation services for enterprise clients",
        "Managed infrastructure setup for enterprise test management tools (Quality Center, LoadRunner)",
        "Developed and executed performance testing scripts in C, Java, and .NET, identifying bottlenecks and validating systems",
        "Partnered with business and IT stakeholders to define service expectations and execute structured tests",
        "Provided risk-informed recommendations for improving system stability and compliance"
      ],
      technologies: ["Quality Center", "LoadRunner", "C", "Java", ".NET", "Performance Testing", "Test Management"]
    },
    {
      title: "Programmer Analyst",
      company: "EON Computers",
      period: "July 1998 - January 2000",
      location: "Mumbai, India",
      description: "Analyzed, designed, and developed systems using case tools, AWT components, and Servlets, ensuring reliable functionality and alignment with client needs.",
      achievements: [
        "Analyzed, designed, and developed systems using case tools, AWT components, and Servlets",
        "Supported both front-end design and back-end integration, balancing usability with data integrity",
        "Ensured reliable functionality and alignment with client needs",
        "Developed strong foundation in software engineering principles and practices"
      ],
      technologies: ["Case Tools", "AWT", "Servlets", "Java", "System Design", "Front-end/Back-end Development"]
    }
  ]

  const certifications = [
    {
      name: "AWS Solutions Architect Professional",
      issuer: "Amazon Web Services",
      year: "2023"
    },
    {
      name: "Google Cloud Professional Cloud Architect",
      issuer: "Google Cloud",
      year: "2022"
    },
    {
      name: "Certified Kubernetes Administrator (CKA)",
      issuer: "Cloud Native Computing Foundation",
      year: "2021"
    },
    {
      name: "PMP - Project Management Professional",
      issuer: "Project Management Institute",
      year: "2020"
    }
  ]

  return (
    <section id="experience" className="section-padding bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Experience & Achievements
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-indigo-600 mx-auto mb-8" />
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            A proven track record spanning over 20 years of delivering exceptional results and leading 
            technology teams to success across various industries.
          </p>
        </motion.div>

        {/* Experience Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-indigo-500" />

          {experiences.map((experience, index) => (
            <motion.div
              key={`${experience.company}-${experience.period}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`relative mb-12 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              } flex flex-col md:flex-row items-center`}
            >
              {/* Timeline Dot */}
              <div className="absolute left-4 md:left-1/2 transform md:-translate-x-1/2 w-4 h-4 bg-primary-500 rounded-full border-4 border-white shadow-lg z-10" />

              {/* Content */}
              <div className={`ml-12 md:ml-0 md:w-5/12 ${
                index % 2 === 0 ? 'md:pr-8' : 'md:pl-8 md:order-2'
              }`}>
                <div className="bg-white rounded-2xl p-8 shadow-sm hover-lift">
                  <div className="flex items-center gap-2 text-primary-600 mb-2">
                    <Calendar size={16} />
                    <span className="text-sm font-medium">{experience.period}</span>
                  </div>
                  
                  <h3 className="text-2xl font-playfair font-semibold text-gray-900 mb-2">
                    {experience.title}
                  </h3>
                  
                  <div className="flex items-center gap-2 text-gray-600 mb-3">
                    <Building size={16} />
                    <span className="font-medium">{experience.company}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-gray-500 mb-4">
                    <MapPin size={16} />
                    <span>{experience.location}</span>
                  </div>
                  
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {experience.description}
                  </p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Award size={16} className="text-primary-600" />
                      Key Achievements
                    </h4>
                    <ul className="space-y-1">
                      {experience.achievements.map((achievement, achievementIndex) => (
                        <li key={achievementIndex} className="text-sm text-gray-600 flex items-start gap-2">
                          <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mt-2 flex-shrink-0" />
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {experience.technologies.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <h3 className="text-3xl font-playfair font-semibold text-gray-900 text-center mb-12">
            Certifications & Credentials
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl shadow-sm hover-lift text-center"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-indigo-500 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Award className="text-white" size={24} />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2 text-sm">{cert.name}</h4>
                <p className="text-gray-600 text-sm mb-1">{cert.issuer}</p>
                <p className="text-primary-600 font-medium text-sm">{cert.year}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default Experience
